import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import PhoneInput from "react-phone-input-2";
import classNames from "classnames";
import { useDispatch } from "react-redux";
import { useNavigation } from "utils/navigate";
import { toast } from "Layout/slice";
import {
  SignupFlowFooter,
  SignupFlowContent,
  SignupFlowWrapperContainer,
  FooterRight,
} from "../../Components/SignupFlowWrapper.styles";
import { Text } from "style/v2/components/Typography";
import { Button } from "components/Button";
import { Form } from "components/Form";
import { Loader } from "components/Loader";
import {
  PhoneVerificationContainer,
  FormSection,
  PhoneInputWrapper,
  OtpContainer,
  OtpInput,
  SendCodeText,
} from "./PhoneVerification.styles";
import {
  resendVerificationCode,
  verifyNumber,
  getKycDetails,
  submitNumber,
} from "features/SurmountAccount/service";

const validationSchema = Yup.object({
  phoneNumber: Yup.string().required("Phone number is required"),
  otp: Yup.string()
    .matches(/^\d{6}$/, "OTP must be 6 digits")
    .required("OTP is required"),
});

const PhoneVerification = () => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [currentCountryCode, setCurrentCountryCode] = useState("US");
  const [phoneVerificationData, setPhoneVerificationData] = useState({
    phoneNumber: "",
    countryCode: "US",
    isVerified: false,
  });
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const {
    navigateToSignupSurmountAccountPersonalDetails,
    navigateToSignupSurmountRecommendationsKycQuestions,
  } = useNavigation();

  // Load saved phone verification data from localStorage on mount
  useEffect(() => {
    try {
      const savedData = localStorage.getItem("phoneVerification_data");
      if (savedData) {
        const parsed = JSON.parse(savedData);
        setPhoneVerificationData(parsed);
        setCurrentCountryCode(parsed.countryCode || "US");
      }
    } catch (error) {
      console.error(
        "Error loading phone verification data from localStorage:",
        error
      );
    } finally {
      setIsDataLoaded(true);
    }
  }, []);

  // Save phone verification data to localStorage when it changes
  useEffect(() => {
    if (isDataLoaded) {
      try {
        localStorage.setItem(
          "phoneVerification_data",
          JSON.stringify(phoneVerificationData)
        );
      } catch (error) {
        console.error(
          "Error saving phone verification data to localStorage:",
          error
        );
      }
    }
  }, [phoneVerificationData, isDataLoaded]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      phoneNumber: phoneVerificationData.phoneNumber || "",
      otp: "",
      countryCode: phoneVerificationData.countryCode || "US",
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        // If phone is already verified and hasn't changed, skip verification
        if (phoneVerificationData.isVerified && !hasPhoneNumberChanged()) {
          navigateToSignupSurmountRecommendationsKycQuestions();
          return;
        }

        // Otherwise, verify the phone number
        await verifyNumber({
          phone_number: values.phoneNumber,
          otp: values.otp,
          country_code: values.countryCode,
        });

        // Save verification state to localStorage
        setPhoneVerificationData({
          phoneNumber: values.phoneNumber,
          countryCode: values.countryCode,
          isVerified: true,
        });

        navigateToSignupSurmountRecommendationsKycQuestions();
      } catch (error) {
        // Handle specific error for already verified phone number
        if (
          error?.response?.data?.message === "Phone number is already verified."
        ) {
          dispatch(
            toast({
              type: "error",
              body: "This phone number is already verified. You can continue to the next step.",
            })
          );

          // Mark as verified and allow continuation
          setPhoneVerificationData({
            phoneNumber: values.phoneNumber,
            countryCode: values.countryCode,
            isVerified: true,
          });

          // Navigate to next page since phone is already verified
          navigateToSignupSurmountRecommendationsKycQuestions();
        } else {
          // Show generic error for other cases
          dispatch(
            toast({
              type: "error",
              body:
                error?.response?.data?.message ||
                "Failed to verify phone number. Please try again.",
            })
          );
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Update formik values when data is loaded from localStorage
  useEffect(() => {
    if (isDataLoaded && phoneVerificationData.phoneNumber) {
      formik.setFieldValue("phoneNumber", phoneVerificationData.phoneNumber);
      formik.setFieldValue("countryCode", phoneVerificationData.countryCode);
    }
  }, [
    isDataLoaded,
    phoneVerificationData.phoneNumber,
    phoneVerificationData.countryCode,
  ]);

  useEffect(() => {
    const flagButton = document.querySelector(
      ".react-tel-input .selected-flag"
    );
    if (flagButton) {
      flagButton.setAttribute("data-country-code", currentCountryCode);
    }
  }, [currentCountryCode]);

  // Check if phone number has changed from saved number
  const hasPhoneNumberChanged = () => {
    return (
      formik.values.phoneNumber !== phoneVerificationData.phoneNumber ||
      formik.values.countryCode !== phoneVerificationData.countryCode
    );
  };

  // Check if we need to verify the phone number
  const needsVerification = () => {
    return !phoneVerificationData.isVerified || hasPhoneNumberChanged();
  };

  // Save phone number to localStorage when it changes
  const savePhoneNumberToStorage = (phoneNumber, countryCode) => {
    setPhoneVerificationData((prev) => ({
      ...prev,
      phoneNumber,
      countryCode,
    }));
  };

  const isPhoneValid = (phone) => {
    const digitsOnly = phone.replace(/\D/g, "");
    return digitsOnly.length >= 10;
  };

  const handleSendCode = async () => {
    if (
      !formik.values.phoneNumber ||
      !isPhoneValid(formik.values.phoneNumber)
    ) {
      formik.setFieldTouched("phoneNumber", true);
      return;
    }

    setIsSendingCode(true);
    try {
      await submitNumber({
        phone_number: `+${formik.values.phoneNumber}`,
      });

      await getKycDetails();

      setCodeSent(true);

      // If phone number changed, reset verification state
      if (hasPhoneNumberChanged()) {
        setPhoneVerificationData((prev) => ({
          ...prev,
          isVerified: false,
        }));
      }
    } catch (error) {
      console.error("Error in send code flow:", error);

      // Handle specific error for already verified phone number
      if (
        error?.response?.data?.message === "Phone number is already verified."
      ) {
        dispatch(
          toast({
            type: "error",
            body: "This phone number is already verified. You can continue to the next step.",
          })
        );

        // Mark as verified and allow continuation
        setPhoneVerificationData({
          phoneNumber: formik.values.phoneNumber,
          countryCode: formik.values.countryCode,
          isVerified: true,
        });

        // Navigate to next page since phone is already verified
        navigateToSignupSurmountRecommendationsKycQuestions();
      } else {
        // Show generic error for other cases
        dispatch(
          toast({
            type: "error",
            body:
              error?.response?.data?.message ||
              "Failed to send verification code. Please try again.",
          })
        );
      }
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleResendCode = async () => {
    if (
      !formik.values.phoneNumber ||
      !isPhoneValid(formik.values.phoneNumber)
    ) {
      formik.setFieldTouched("phoneNumber", true);
      return;
    }

    setIsSendingCode(true);
    try {
      await resendVerificationCode({
        phone_number: `+${formik.values.phoneNumber}`,
      });

      setCodeSent(true);
    } catch (error) {
      console.error("Error sending verification code:", error);

      // Handle specific error for already verified phone number
      if (
        error?.response?.data?.message === "Phone number is already verified."
      ) {
        dispatch(
          toast({
            type: "error",
            body: "This phone number is already verified. You can continue to the next step.",
          })
        );

        // Mark as verified and allow continuation
        setPhoneVerificationData({
          phoneNumber: formik.values.phoneNumber,
          countryCode: formik.values.countryCode,
          isVerified: true,
        });

        // Navigate to next page since phone is already verified
        navigateToSignupSurmountRecommendationsKycQuestions();
      } else {
        // Show generic error for other cases
        dispatch(
          toast({
            type: "error",
            body:
              error?.response?.data?.message ||
              "Failed to resend verification code. Please try again.",
          })
        );
      }
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleGoBack = () => {
    navigateToSignupSurmountAccountPersonalDetails();
  };

  const handleOtpChange = (e, index) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    const otpArray = formik.values.otp.split("");
    otpArray[index] = val;
    const newOtp = otpArray.join("");

    formik.setFieldValue("otp", newOtp);

    if (val && index < 5) {
      const nextInput = e.target.parentNode.querySelector(
        `input:nth-child(${index + 2})`
      );
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData
      .getData("text")
      .slice(0, 6)
      .replace(/[^0-9]/g, "");
    formik.setFieldValue("otp", paste);

    const lastIndex = Math.min(paste.length - 1, 5);
    const targetInput = e.target.parentNode.querySelector(
      `input:nth-child(${lastIndex + 2})`
    );
    if (targetInput) targetInput.focus();
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      const prevInput = e.target.parentNode.querySelector(
        `input:nth-child(${index})`
      );
      if (prevInput) {
        prevInput.focus();
        prevInput.select();
      }
    }
  };

  return (
    <SignupFlowWrapperContainer>
      <SignupFlowContent>
        <PhoneVerificationContainer>
          <Text variant="cardHeading" as="h4" mb="8px">
            {needsVerification()
              ? "Please verify your phone number"
              : "Phone number verified"}
          </Text>
          <Text variant="body" color="tertiary" mb="20px">
            {needsVerification()
              ? "We will confirm your number by sending a verification code to your phone"
              : "Your phone number has been verified. You can continue to the next step."}
          </Text>

          <Form onSubmit={formik.handleSubmit}>
            <FormSection>
              <Row
                style={{ marginBottom: "1rem" }}
                className="align-items-center"
              >
                <Col md={10}>
                  <PhoneInputWrapper>
                    <PhoneInput
                      country="us"
                      placeholder="+1 (555) 000-0000"
                      inputProps={{ name: "phoneNumber" }}
                      enableSearch
                      value={formik.values.phoneNumber}
                      onChange={(phone, country) => {
                        formik.setFieldValue("phoneNumber", phone);
                        const countryCodeUpper =
                          country.countryCode.toUpperCase();
                        formik.setFieldValue("countryCode", countryCodeUpper);
                        setCurrentCountryCode(countryCodeUpper);

                        // Save phone number to localStorage
                        savePhoneNumberToStorage(phone, countryCodeUpper);

                        // Reset OTP when phone number changes
                        formik.setFieldValue("otp", "");
                        setCodeSent(false);
                      }}
                      inputClass={classNames("phone-form-field", {
                        "is-invalid":
                          formik.touched.phoneNumber &&
                          formik.errors.phoneNumber,
                      })}
                      buttonClass="country-code-btn"
                    />
                    {formik.touched.phoneNumber &&
                      formik.errors.phoneNumber && (
                        <div className="form-error">
                          {formik.errors.phoneNumber}
                        </div>
                      )}
                  </PhoneInputWrapper>
                </Col>
                <Col md={2} className="text-end">
                  <SendCodeText
                    type="button"
                    onClick={codeSent ? handleResendCode : handleSendCode}
                    disabled={
                      isSendingCode ||
                      !formik.values.phoneNumber ||
                      !isPhoneValid(formik.values.phoneNumber) ||
                      (!needsVerification() && phoneVerificationData.isVerified)
                    }
                  >
                    {isSendingCode ? (
                      <Loader className="on-btn" />
                    ) : codeSent ? (
                      "Resend Code"
                    ) : needsVerification() ? (
                      "Send Code"
                    ) : (
                      "Verified"
                    )}
                  </SendCodeText>
                </Col>
              </Row>

              {needsVerification() && (
                <Row>
                  <Col>
                    <div className="otp-title">Secure Code</div>
                    <OtpContainer>
                      {Array.from({ length: 6 }).map((_, index) => (
                        <OtpInput
                          key={index}
                          type="text"
                          maxLength={1}
                          value={formik.values.otp[index] || ""}
                          onChange={(e) => handleOtpChange(e, index)}
                          onPaste={handleOtpPaste}
                          onKeyDown={(e) => handleOtpKeyDown(e, index)}
                          className={classNames({
                            "is-invalid":
                              formik.touched.otp && formik.errors.otp,
                          })}
                        />
                      ))}
                    </OtpContainer>
                    {formik.touched.otp && formik.errors.otp && (
                      <div className="form-error">{formik.errors.otp}</div>
                    )}
                    <div className="otp-description">
                      Please check your secure code and try again! You can
                      request a new code
                    </div>
                  </Col>
                </Row>
              )}
            </FormSection>
          </Form>
        </PhoneVerificationContainer>
      </SignupFlowContent>

      <SignupFlowFooter>
        <FooterRight>
          <Button variant="secondary" onClick={handleGoBack}>
            Go Back
          </Button>
          <Button
            variant="primary"
            onClick={formik.handleSubmit}
            disabled={
              isSubmitting ||
              (needsVerification() &&
                (!codeSent ||
                  !formik.values.otp ||
                  formik.values.otp.length !== 6))
            }
          >
            {isSubmitting ? <Loader className="on-btn" /> : "Continue"}
          </Button>
        </FooterRight>
      </SignupFlowFooter>
    </SignupFlowWrapperContainer>
  );
};

export default PhoneVerification;
